/*
 * Shared FIFO promise-semaphore for bounding client-side fetch fan-out
 * (perf follow-up BOARDROOM-2026-09-01-225419 item 2). A 241-order account's
 * Orders page mounts one OrderCard per order, and each mount fires
 * useOrderSeats → fetchOrderAllocations — 241 simultaneous GETs. That burst
 * rate-limited/500'd at the gateway and EVERY card degraded together. Bounding
 * the fan-out keeps the burst browser- and gateway-friendly while changing
 * NOTHING about what is fetched — callers just wait their turn.
 *
 * SCOPE-CAPTURE DISCIPLINE (WU6 lesson, MY-2026-09-01-211410): the limiter
 * closure holds ONLY the counter and an array of bare resolve thunks — no
 * request payloads, no responses, no component scope. What each queued TASK
 * captures is the caller's business; keep those closures small too.
 */

/**
 * Create a FIFO concurrency limiter. `limited(task)` runs `task` immediately
 * if fewer than `limit` tasks are in flight, otherwise queues it (strict FIFO)
 * until a slot frees. The slot is ALWAYS released when the task settles —
 * resolve OR reject — so a failing task can never deadlock the queue; its
 * rejection propagates to the caller untouched.
 */
export function createFifoLimiter(limit: number): <T>(task: () => Promise<T>) => Promise<T> {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new Error(`limiter limit must be a positive integer, got ${limit}`)
  }
  let active = 0
  const waiters: Array<() => void> = []
  const release = (): void => {
    const next = waiters.shift()
    // Hand the freed slot STRAIGHT to the oldest waiter (active count is
    // unchanged: one task out, one in). Only with an empty queue does the
    // slot actually open up.
    if (next) next()
    else active -= 1
  }
  return async function limited<T>(task: () => Promise<T>): Promise<T> {
    if (active < limit) active += 1
    else await new Promise<void>((resolve) => waiters.push(resolve))
    try {
      return await task()
    } finally {
      release() // always — failure must free the slot (no deadlock)
    }
  }
}
